import React, { Component } from 'react';
import ReactDOM from 'react-dom';

import { createIntl, createIntlCache, RawIntlProvider } from 'react-intl';
import { Provider } from 'react-redux';
import { lazify, appendGMapsLibraryScript, validateProfilingConsent, sendPostMessage } from 'utils';

// import registerServiceWorker from './registerServiceWorker'
// registerServiceWorker()

import {
  ui as uiSelectors,
  property as propertySelectors,
  flyers as flyersSelectors,
} from 'flux/selectors';

import {
  application as applicationActions,
  property as propertyActions,
  tracking as trackingActions,
} from 'flux/actions';

import { getI18nMessages, getI18nKebbabFormat } from 'utils/i18n';

import globalErrorHandler from 'errorHandler';
import configureStore from 'flux';

import Splash from 'splash';
import { CONFIGURABLE_NAME_ADD_TO_CART } from 'utils/configurables';
import { eventFields } from 'utils/eventFields';

const getIntlDetails = (state) => {
  const language = uiSelectors.selectLanguage(state);
  const I18nKebbabFormatLanguage = getI18nKebbabFormat(language);

  const defaultMessages = getI18nMessages(language);
  const propertyMessages = propertySelectors.selectPropertyMessages(state);
  const messages = {
    ...defaultMessages,
    ...propertyMessages,
  };

  const cacheIntl = createIntlCache();
  const intl = createIntl(
    {
      locale: I18nKebbabFormatLanguage,
      messages,
    },
    cacheIntl,
  );

  return { intl };
};

const initializeApp = (resources = {}) => {
  // the property is already fetched here, so the splash shown while the App chunk loads can honour its extras
  // (and the query string overrides, read from the current location as the store is not created yet)
  const propertyState = {
    property: resources.property || {},
    router: { initialLocation: window.location },
  };
  const App = lazify(
    () => import('./App'),
    propertySelectors.selectHideThumbnailOnFlyerLoading(propertyState) ? (
      <Splash hideThumbnail logoUrl={propertySelectors.selectPropertyLogoUrl(propertyState)} />
    ) : (
      <Splash />
    ),
  );

  class AppProvider extends Component {
    constructor(props) {
      super(props);

      /*
      Passing this.errorHandler() as errorCallback, allow us to grab also unexpected Redux errors
      inside the AppProvider component, showing the Splash error page also for them.
      */
      this.errorHandler = this.errorHandler.bind(this);
      const stateResourcers = {
        ...resources,
        stores: {
          list: {},
          idBy_id: {},
          idByCustomerCode: {},
        },
      };
      // const { flyers: { list: flyersList } } = resources
      // const flyersShapes = {}
      // const stores = {
      //   list: {},
      //   idBy_id: {},
      //   idByCustomerCode: {},
      // }

      // for (const [flyerId, flyer] of Object.entries(flyersList)) {
      //   flyersShapes[flyerId] = flyer.shape
      //   for (const [storeId, store] of Object.entries(flyer.stores)) {
      //     stores.list[storeId] = store

      //     if (store._id) { // eslint-disable-line no-underscore-dangle
      //       stores.idBy_id[store._id] = store.id // eslint-disable-line no-underscore-dangle
      //     }

      //     const customerCode = store.extras?.customer_code
      //     if (customerCode) {
      //       stores.idByCustomerCode[customerCode] = store.id
      //     }
      //   }
      // }
      // stateResourcers.flyers.shapes = flyersShapes
      // stateResourcers.stores = stores
      this.store = configureStore(stateResourcers, this.errorHandler);

      // set i18n variables
      const intlDetails = getIntlDetails(this.store.getState());
      this.intl = intlDetails.intl;

      this.state = { hasError: false };

      this.appendGoogleLibraries();

      this.onMessage = this.onMessage.bind(this);
    }

    static getDerivedStateFromError() {
      // Update state so the next render will show the fallback UI.
      return { hasError: true };
    }

    componentDidMount() {
      this.store.dispatch(applicationActions.initApplication());

      window.addEventListener('message', this.onMessage);
    }

    componentDidCatch(error) {
      this.errorHandler(error);
    }

    componentWillUnmount() {
      if (this.onMessage) {
        window.removeEventListener('message', this.onMessage);
      }
    }

    onMessage(e) {
      const { eventType, configurableName, data } = e.data || {};

      if (eventType === 'profilingConsent:update') {
        const profilingConsent = validateProfilingConsent(data.profilingConsent);
        this.store.dispatch(trackingActions.profileConsentUpdate(profilingConsent));
        this.store.dispatch(propertyActions.toggleProfilingConsent(profilingConsent));
        this.store.dispatch(propertyActions.setProfilingConsent(profilingConsent));
      } else if (eventType === 'retailerDataLayerNextEvent') {
        // retailerDataLayerNextEvent is used in next
        const state = this.store.getState();
        const targetOrigin = uiSelectors.selectPostMessageTargetOrigin(state);
        sendPostMessage(
          'retailerDataLayerEvent',
          eventFields.flyer.stripNextFromCustomLabel(data),
          targetOrigin,
        ); // retailerDataLayerEvent is used in the init
      } else if (eventType === 'configurableEvent') {
        // configurableEvent is used in next

        if (configurableName === CONFIGURABLE_NAME_ADD_TO_CART) {
          const state = this.store.getState();
          const targetOrigin = uiSelectors.selectPostMessageTargetOrigin(state);
          const currentFlyer = flyersSelectors.selectCurrentFlyer(state);
          sendPostMessage(
            'addToCart',
            eventFields.flyer.stripNextFromCustomLabel({
              id: currentFlyer?.id ?? '',
              ...eventFields.flyer.getCustomLabel(currentFlyer),
              ...data,
            }),
            targetOrigin,
          ); // addToCart is used in the init
        }
      }
    }

    async appendGoogleLibraries() {
      // Google Maps library initialization
      const { REACT_APP_GOOGLE_MAPS_API_KEY, REACT_APP_ENV } = process.env;
      let { google_maps_api_key: googleMapsAPIKey } = resources.property.extras;
      googleMapsAPIKey =
        REACT_APP_ENV === 'dev' || REACT_APP_ENV === 'stg'
          ? REACT_APP_GOOGLE_MAPS_API_KEY
          : googleMapsAPIKey;
      // eslint-disable-next-line
      await appendGMapsLibraryScript(googleMapsAPIKey, this.language).catch(console.error);
    }

    errorHandler(error) {
      globalErrorHandler(error);
      this.setState({ hasError: true });
    }

    render() {
      const { hasError } = this.state;

      return hasError ? (
        <Splash type="error" />
      ) : (
        <Provider store={this.store}>
          <RawIntlProvider value={this.intl}>
            <App />
          </RawIntlProvider>
        </Provider>
      );
    }
  }

  ReactDOM.render(<AppProvider />, document.getElementById('root'));
};

export default initializeApp;
